import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Stack
} from '@mui/material';

// Импортируем отдельный компонент панели инструментов
import EditorToolbar from './EditorToolbar';
// Импортируем инструменты таблицы
import TableTools from './TableTools';

// Библиотеки для экспорта
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import htmlDocx from 'html-docx-js/dist/html-docx';
import api from '../../api/api';

// Стили для A4 документа и редактора
const getDocumentStyles = (margins) => `
  .document-canvas {
    width: 210mm;
    padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
    margin: 0 auto;
    background: white;
    box-shadow: 0 0 10px rgba(0,0,0,0.1);
    position: relative;
  }
  
  .document-canvas:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid #e0e0e0;
    pointer-events: none;
  }
  
  .editor-container {
    font-family: 'Times New Roman', serif;
    font-size: 14px;
    line-height: 1.6;
    color: #000;
    outline: none;
    padding: 0;
    border: none;
    overflow-wrap: break-word;
  }
  
  .editor-container h1, .editor-container h2, .editor-container h3 {
    margin: 20px 0 10px 0;
  }
  
  .editor-container p {
    margin: 10px 0;
  }
  
  .editor-container div[style*="text-align"] {
    margin: 10px 0;
  }
  
  .editor-container span[style*="font-size"] {
    display: inline;
  }
  
  .editor-container div[style*="line-height"] {
    margin: 10px 0;
  }
  
  .editor-container table {
    border-collapse: collapse !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 15px 0 !important;
    border: 2px solid #000 !important;
    background: white !important;
    table-layout: fixed !important; /* Фиксированная ширина колонок */
    box-sizing: border-box !important;
  }
  
  .editor-container table td,
  .editor-container table th {
    border: 1px solid #000 !important;
    padding: 12px !important;
    text-align: left !important;
    vertical-align: top !important;
    background: white !important;
    word-wrap: break-word !important; /* Перенос длинных слов */
    word-break: break-word !important; /* Разрыв слов при необходимости */
    overflow-wrap: break-word !important; /* Совместимость */
    position: relative !important;
    min-width: 50px !important;
    min-height: 30px !important;
    box-sizing: border-box !important;
    cursor: text !important; /* Курсор для ввода текста */
  }
  
  .editor-container table th:hover {
    background-color: #e8f4fd !important;
  }
  
  .editor-container table th {
    background-color: #f0f0f0 !important;
    font-weight: bold !important;
  }
  
  /* Обеспечиваем видимость пустых ячеек */
  .editor-container table td:empty::before,
  .editor-container table th:empty::before {
    content: '\\00a0'; /* Неразрывный пробел для пустых ячеек */
    color: transparent; /* Делаем невидимым */
  }
  
  /* Ограничиваем размер шрифта в таблицах */
  .editor-container table td *,
  .editor-container table th * {
    max-width: 100% !important;
    word-wrap: break-word !important;
    line-height: 1.4 !important;
  }
  
  /* Ограничиваем размер шрифта в ячейках */
  .editor-container table td span,
  .editor-container table th span {
    display: inline !important;
    max-width: 100% !important;
  }
  
  .editor-container ul, .editor-container ol {
    margin: 10px 0;
    padding-left: 30px;
  }
  
  .editor-container blockquote {
    margin: 10px 0;
    padding: 10px 20px;
    border-left: 3px solid #ccc;
    background: #f9f9f9;
  }
  
  @media print {
    .document-canvas {
      box-shadow: none;
      margin: 0;
      padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
    }
    
    .toolbar {
      display: none !important;
    }
    
    @page {
      size: A4;
      margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
    }
  }
`;

// Системные переменные для договоров
const SystemVariables = [
  { name: 'client_name', description: 'ФИО клиента' },
  { name: 'client_phone', description: 'Телефон клиента' },
  { name: 'client_email', description: 'Email клиента' },
  { name: 'passport_series', description: 'Серия паспорта' },
  { name: 'passport_number', description: 'Номер паспорта' },
  { name: 'passport_issued_by', description: 'Кем выдан паспорт' },
  { name: 'passport_issued_date', description: 'Дата выдачи паспорта' },
  { name: 'client_address', description: 'Адрес регистрации клиента' },
  { name: 'asset_name', description: 'Название актива' },
  { name: 'rental_price', description: 'Стоимость аренды' },
  { name: 'total_amount', description: 'Общая сумма сделки' },
  { name: 'start_date', description: 'Дата начала аренды' },
  { name: 'end_date', description: 'Дата окончания аренды' },
  { name: 'rental_point', description: 'Точка аренды' },
  { name: 'deposit', description: 'Размер залога' },
  { name: 'contract_number', description: 'Номер договора' },
  { name: 'contract_date', description: 'Дата договора' },
  { name: 'ITEMS_TABLE', description: 'Динамическая таблица товаров (автоматически генерирует строки для каждого товара)' }
];

const normalizeMargins = (value) => Object.fromEntries(['top', 'bottom', 'left', 'right'].map(key => {
  const candidate = value?.[key];
  return [key, candidate != null && Number.isFinite(Number(candidate)) ? Math.max(0, Math.min(50, Number(candidate))) : 20];
}));

// Editing controls must never be saved as part of a customer's document.
const documentHTML = (element) => {
  const copy = element.cloneNode(true);
  copy.querySelectorAll('.table-column-resizer, .column-resizer, .row-resizer').forEach(control => control.remove());
  copy.querySelectorAll('table').forEach(table => {
    table.style.boxShadow = '';
    if (table.style.border.toLowerCase().includes('#2196f3')) table.style.border = '2px solid #000';
  });
  return copy.innerHTML;
};

function DocumentEditor({ 
  templateId,
  initialContent = '<p>Начните создание документа...</p>',
  onContentChange,
  onSave,
  showVariables = true,
  variables = SystemVariables,
  readOnly = false // Новый параметр для режима только просмотра
}) {
  const [content, setContent] = useState(initialContent);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const editorRef = useRef(null);
  const savingRef = useRef(false);
  const templateRequestRef = useRef(0);
  const historyRef = useRef({ entries: [initialContent], index: 0 });
  const historyTimer = useRef(null);
  
  // История для undo/redo
  const [history, setHistory] = useState([initialContent]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false); // Флаг для отслеживания операций отката
  
  // Инструменты таблицы
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableToolsPosition, setTableToolsPosition] = useState(null);
  
  // Модальные окна
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [marginsDialogOpen, setMarginsDialogOpen] = useState(false);
  
  // Параметры таблицы
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  // Параметры отступов страницы (в мм)
  const [pageMargins, setPageMargins] = useState(() => {
    // Пытаемся загрузить отступы из localStorage
    const savedMargins = localStorage.getItem('documentEditor_pageMargins');
    if (savedMargins) {
      try {
        return normalizeMargins(JSON.parse(savedMargins));
      } catch (error) {
        console.error('Ошибка загрузки сохраненных отступов:', error);
      }
    }
    // Возвращаем дефолтные значения
    return {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20
    };
  });

  const [draftMargins, setDraftMargins] = useState(pageMargins);

  const addToHistory = useCallback((newContent) => {
    const current = historyRef.current;
    if (current.entries[current.index] === newContent) return;
    const entries = [...current.entries.slice(0, current.index + 1), newContent].slice(-50);
    historyRef.current = { entries, index: entries.length - 1 };
    setHistory(entries);
    setHistoryIndex(entries.length - 1);
  }, []);

  const debouncedAddToHistory = useCallback((newContent) => {
    clearTimeout(historyTimer.current);
    historyTimer.current = setTimeout(() => addToHistory(newContent), 1000);
  }, [addToHistory]);

  useEffect(() => () => clearTimeout(historyTimer.current), []);

  const resetHistory = useCallback((newContent) => {
    clearTimeout(historyTimer.current);
    historyRef.current = { entries: [newContent], index: 0 };
    setHistory([newContent]);
    setHistoryIndex(0);
  }, []);

  // ИСПРАВЛЕННАЯ функция принудительного обновления контента
  const forceUpdateContent = useCallback(() => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      
      // НЕ добавляем в историю при операциях отката/повтора
      if (!isUndoRedoOperation && history[historyIndex] !== newContent) {
        addToHistory(newContent);
      }
      
      if (onContentChange) {
        onContentChange(newContent);
      }
    }
  }, [onContentChange, addToHistory, history, historyIndex, isUndoRedoOperation]);

  // Функция настройки таблиц - НОВЫЙ ПОДХОД БЕЗ РЕСАЙЗЕРОВ
  const setupTables = useCallback(() => {
    if (!editorRef.current || readOnly) return;
    
    const tables = editorRef.current.querySelectorAll('table');
    
    tables.forEach(table => {
      // Удаляем старые обработчики и ресайзеры
      table.removeEventListener('click', handleTableClick);
      table.querySelectorAll('.column-resizer, .row-resizer').forEach(el => el.remove());
      
      // Настраиваем базовые стили таблицы - ДИНАМИЧЕСКАЯ ШИРИНА
      table.style.tableLayout = 'fixed';
      table.style.width = '100%';
      table.style.maxWidth = '100%';
      table.style.cursor = 'pointer';
      
      // Устанавливаем равномерную ширину колонок ТОЛЬКО если размеры вообще не заданы
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        const cells = firstRow.querySelectorAll('td, th');
        
        // Проверяем, есть ли уже заданные размеры (в любых единицах)
        const hasWidths = Array.from(cells).some(cell => 
          cell.style.width && cell.style.width !== 'auto'
        );
        
        // Применяем ко всем ячейкам таблицы
        const allRows = table.querySelectorAll('tr');
        allRows.forEach(row => {
          const rowCells = row.querySelectorAll('td, th');
          rowCells.forEach((cell, index) => {
            // ТОЛЬКО если нет заданных размеров, устанавливаем проценты
            if (!hasWidths && (!cell.style.width || cell.style.width === 'auto')) {
              const cellWidthPercent = Math.floor(100 / cells.length);
              cell.style.width = cellWidthPercent + '%';
              cell.style.maxWidth = cellWidthPercent + '%';
              cell.style.minWidth = cellWidthPercent + '%';
            }
            
            // Базовые стили для всех ячеек
            cell.style.resize = 'none';
            cell.style.overflow = 'hidden';
            cell.style.wordWrap = 'break-word';
            cell.style.wordBreak = 'break-word';
            cell.style.height = 'auto'; // Высота автоматическая под содержимое
          });
        });
      }
      
      // Добавляем обработчик клика для открытия инструментов
      table.addEventListener('click', handleTableClick);
      
      // Добавляем hover эффект
      table.addEventListener('mouseenter', () => {
        if (!selectedTable) {
          table.style.boxShadow = '0 0 0 2px rgba(33, 150, 243, 0.3)';
        }
      });
      
      table.addEventListener('mouseleave', () => {
        if (table !== selectedTable) {
          table.style.boxShadow = '';
        }
      });
    });
  }, [readOnly]);

  // Обработчик клика по таблице
  const handleTableClick = useCallback((event) => {
    event.stopPropagation();
    
    const table = event.currentTarget;
    const rect = table.getBoundingClientRect();
    
    // Показываем инструменты таблицы
    setSelectedTable(table);
    setTableToolsPosition({
      x: rect.right + 10, // Справа от таблицы
      y: rect.top
    });
    
    // Выделяем таблицу визуально
    const allTables = editorRef.current.querySelectorAll('table');
    allTables.forEach(t => t.style.boxShadow = '');
    table.style.boxShadow = '0 0 0 2px #2196F3';
    
  }, []);

  // Закрытие инструментов таблицы
  const handleCloseTableTools = useCallback(() => {
    // Убираем ресайзеры с текущей таблицы если они есть
    if (selectedTable) {
      selectedTable.querySelectorAll('.table-column-resizer').forEach(resizer => {
        resizer.remove();
      });
      selectedTable.style.border = '2px solid #000';
      selectedTable.style.boxShadow = '';
    }
    
    setSelectedTable(null);
    setTableToolsPosition(null);
    
    // Убираем выделение
    if (editorRef.current) {
      const allTables = editorRef.current.querySelectorAll('table');
      allTables.forEach(t => t.style.boxShadow = '');
    }
  }, [selectedTable]);

  // Обновление таблицы после изменений
  const handleTableUpdate = useCallback(() => {
    forceUpdateContent();
  }, [forceUpdateContent]);

  // ИСПРАВЛЕННАЯ функция отмены
  const handleUndo = useCallback(() => {
    clearTimeout(historyTimer.current);
    const currentHTML = editorRef.current?.innerHTML;
    if (currentHTML != null) addToHistory(currentHTML);
    const currentHistory = historyRef.current;
    if (currentHistory.index > 0) {
      const newIndex = currentHistory.index - 1;
      const previousContent = currentHistory.entries[newIndex];
      
      // Устанавливаем флаг операции отката
      setIsUndoRedoOperation(true);
      
      // Обновляем состояние БЕЗ добавления в историю
      setContent(previousContent);
      setHistoryIndex(newIndex);
      historyRef.current.index = newIndex;
      clearTimeout(historyTimer.current);
      
      if (editorRef.current) {
        editorRef.current.innerHTML = previousContent;
        
        // Добавляем ресайзеры и сбрасываем флаг
        setTimeout(() => {
          if (!editorRef.current) return;
          setupTables();
          setIsUndoRedoOperation(false);
          editorRef.current.focus();
        }, 100);
      } else {
        setIsUndoRedoOperation(false);
      }
      
      if (onContentChange) {
        onContentChange(previousContent);
      }
    }
  }, [addToHistory, onContentChange, setupTables]);

  // ИСПРАВЛЕННАЯ функция повтора
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextContent = history[newIndex];
      
      // Устанавливаем флаг операции повтора
      setIsUndoRedoOperation(true);
      
      // Обновляем состояние БЕЗ добавления в историю
      setContent(nextContent);
      setHistoryIndex(newIndex);
      historyRef.current.index = newIndex;
      clearTimeout(historyTimer.current);
      
      if (editorRef.current) {
        editorRef.current.innerHTML = nextContent;
        
        // Добавляем ресайзеры и сбрасываем флаг
        setTimeout(() => {
          if (!editorRef.current) return;
          setupTables();
          setIsUndoRedoOperation(false);
          editorRef.current.focus();
        }, 100);
      } else {
        setIsUndoRedoOperation(false);
      }
      
      if (onContentChange) {
        onContentChange(nextContent);
      }
    }
  }, [history, historyIndex, onContentChange, setupTables]);

  // Обработка горячих клавиш
  const handleKeyDown = useCallback((event) => {
    if (readOnly) return;
    
    // Обработка Enter для создания новых строк
    if (event.key === 'Enter') {
      // Не препятствуем стандартному поведению Enter
      // Браузер сам создаст новую строку или параграф
      return;
    }
    
    if (event.ctrlKey || event.metaKey) {
      if (event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      } else if (event.key === 'y' || (event.key === 'z' && event.shiftKey)) {
        event.preventDefault();
        handleRedo();
      }
    }
  }, [handleUndo, handleRedo, readOnly]);

  // Загрузка содержимого шаблона при монтировании компонента
  useEffect(() => {
    if (templateId) {
      loadTemplateContent();
    } else {
      setIsLoading(false);
    }
    return () => { templateRequestRef.current += 1; };
  }, [templateId]);

  // Внешнее содержимое задаёт начало истории, включая пустой документ.
  useEffect(() => {
    if (!templateId) {
      setContent(initialContent);
      if (editorRef.current) editorRef.current.innerHTML = initialContent;
      resetHistory(initialContent);
    }
  }, [initialContent, templateId, resetHistory]);

  // Синхронизация DOM с состоянием только при внешних изменениях
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== content) {
      // Проверяем, что изменение пришло не от пользователя (внешнее изменение)
      const currentSelection = window.getSelection();
      const hasSelection = currentSelection.rangeCount > 0;
      
      // Если пользователь активно работает с редактором, не трогаем DOM
      if (!hasSelection || !editorRef.current.contains(currentSelection.focusNode) || document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = content;
        // Добавляем ресайзеры после обновления контента
        setTimeout(() => setupTables(), 50);
      }
    }
  }, [content, setupTables]);

  // Инициализация ресайзеров при первой загрузке
  useEffect(() => {
    if (editorRef.current) {
      setTimeout(() => setupTables(), 200);
    }
  }, [setupTables]);

  // Сохранение отступов в localStorage при их изменении
  useEffect(() => {
    localStorage.setItem('documentEditor_pageMargins', JSON.stringify(pageMargins));
  }, [pageMargins]);

  const loadTemplateContent = async () => {
    if (!templateId) return;
    
    const requestId = ++templateRequestRef.current;
    setIsLoading(true);
    try {
      const response = await api.get(`/documents/templates/${templateId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (requestId !== templateRequestRef.current) return;
      // Если есть содержимое в шаблоне, используем его, иначе используем стандартное
      const templateContent = response.data.content ?? initialContent;
      setContent(templateContent);
      
      // Загружаем сохраненные отступы если они есть
      if (response.data.page_margins) {
        setPageMargins(normalizeMargins(response.data.page_margins));
        // Также сохраняем в localStorage для консистентности
        localStorage.setItem('documentEditor_pageMargins', JSON.stringify(normalizeMargins(response.data.page_margins)));
      }
      
      // Обновляем содержимое в редакторе
      if (editorRef.current) {
        editorRef.current.innerHTML = templateContent;
      }
      
      // Добавляем в историю
      resetHistory(templateContent);
    } catch (error) {
      if (requestId !== templateRequestRef.current) return;
      console.error('Ошибка загрузки шаблона:', error);
      setSnackbar({ 
        open: true, 
        message: 'Ошибка при загрузке шаблона', 
        severity: 'error' 
      });
    } finally {
      if (requestId === templateRequestRef.current) setIsLoading(false);
    }
  };

  // Функция изменения контента
  const handleContentChange = useCallback((event) => {
    const newContent = event.target.innerHTML;
    
    // Обновляем состояние немедленно
    setContent(newContent);
    
    if (onContentChange) {
      onContentChange(newContent);
    }
  }, [onContentChange]);

  // Вставка таблицы
  const handleInsertTable = useCallback(() => {
    setTableDialogOpen(true);
  }, []);

  const confirmInsertTable = useCallback(() => {
    // Создаем HTML таблицу
    let tableHTML = '<table>';
    
    // Создаем заголовок
    tableHTML += '<tr>';
    for (let j = 0; j < tableCols; j++) {
      tableHTML += `<th>Заголовок ${j + 1}</th>`;
    }
    tableHTML += '</tr>';
    
    // Создаем строки с данными
    for (let i = 0; i < tableRows - 1; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        tableHTML += '<td>Данные</td>';
      }
      tableHTML += '</tr>';
    }
    
    tableHTML += '</table><p><br></p>';
    
    // Вставляем в редактор
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, tableHTML);
      forceUpdateContent();
      
      // Добавляем ресайзеры к новой таблице
      setTimeout(() => setupTables(), 100);
    }
    
    setTableDialogOpen(false);
    setSnackbar({ 
      open: true, 
      message: `Таблица ${tableRows}x${tableCols} вставлена! Нажмите на таблицу для настройки размеров.`, 
      severity: 'success' 
    });
  }, [tableRows, tableCols, setupTables, forceUpdateContent]);

  // Вставка переменных
  const handleInsertVariable = useCallback(() => {
    setVariableDialogOpen(true);
  }, []);

  const confirmInsertVariable = useCallback((variable) => {
    const variableSpan = `<span style="font-weight: bold;">{${variable.name}}</span>&nbsp;`;
    
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, variableSpan);
      forceUpdateContent();
    }
    
    setVariableDialogOpen(false);
    setSnackbar({ 
      open: true, 
      message: `Переменная ${variable.description} вставлена!`, 
      severity: 'success' 
    });
  }, [forceUpdateContent]);

  // Настройка отступов страницы
  const handlePageMargins = useCallback(() => {
    setDraftMargins(pageMargins);
    setMarginsDialogOpen(true);
  }, [pageMargins]);

  const handleMarginsChange = useCallback((field, value) => {
    const numValue = Math.max(0, Math.min(50, parseFloat(value) || 0)); // Ограничиваем от 0 до 50мм
    setDraftMargins(prev => ({
      ...prev,
      [field]: numValue
    }));
  }, []);

  const applyMargins = useCallback(() => {
    setPageMargins(draftMargins);
    setMarginsDialogOpen(false);
    setSnackbar({ 
      open: true, 
      message: 'Отступы страницы обновлены!', 
      severity: 'success' 
    });
  }, [draftMargins]);

  // Сохранение документа
  const handleSave = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const currentContent = editorRef.current ? documentHTML(editorRef.current) : content;
    setIsLoading(true);
    try {
      if (templateId) {
        // Сохраняем содержимое шаблона И отступы в API
        await api.put(`/documents/templates/${templateId}`, {
          content: currentContent,
          page_margins: pageMargins // Добавляем отступы в данные шаблона
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }
      
      // Вызываем внешний обработчик, если он есть
      if (onSave) {
        await onSave(currentContent);
      }
      
      setSnackbar({ 
        open: true, 
        message: 'Документ и настройки отступов сохранены!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSnackbar({ 
        open: true, 
        message: 'Ошибка при сохранении документа', 
        severity: 'error' 
      });
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  }, [content, templateId, onSave, pageMargins]);

  // Экспорт в PDF
  const exportToPDF = useCallback(async () => {
    setIsLoading(true);
    let printContainer, styleElement;
    try {
      // Создаем временный контейнер для печати без лишних элементов
      printContainer = document.createElement('div');
      printContainer.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: calc(210mm - ${pageMargins.left}mm - ${pageMargins.right}mm);
        background: white;
        padding: 0;
        font-family: 'Times New Roman', serif;
        line-height: 1.6;
        color: #000;
        box-sizing: border-box;
      `;
      
      // Копируем только контент редактора (сохраняем все inline стили включая размер шрифта)
      const editorContent = editorRef.current ? documentHTML(editorRef.current) : content;
      printContainer.innerHTML = editorContent;
      
      // Очищаем и настраиваем таблицы для правильного отображения
      const tables = printContainer.querySelectorAll('table');
      tables.forEach(table => {
        // Убираем фиксированную ширину
        table.style.width = '100%';
        table.style.maxWidth = '100%';
        table.removeAttribute('width');
        
        // Очищаем стили ячеек от старых пиксельных значений
        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          if (cell.style.width && cell.style.width.includes('px')) {
            cell.style.width = '';
          }
          if (cell.style.maxWidth && cell.style.maxWidth.includes('px')) {
            cell.style.maxWidth = '';
          }
          if (cell.style.minWidth && cell.style.minWidth.includes('px')) {
            cell.style.minWidth = '';
          }
          cell.removeAttribute('width');
        });
      });
      
      // Добавляем стили для таблиц в временный контейнер
      styleElement = document.createElement('style');
      styleElement.textContent = `
        table {
          border-collapse: collapse !important;
          width: 100% !important;
          margin: 15px 0 !important;
          border: 2px solid #000 !important;
        }
        td, th {
          border: 1px solid #000 !important;
          padding: 12px !important;
          text-align: left !important;
          vertical-align: top !important;
        }
        th {
          background-color: #f0f0f0 !important;
          font-weight: bold !important;
        }
      `;
      
      document.body.appendChild(printContainer);
      document.head.appendChild(styleElement);

      // Ждем рендеринга и получаем реальную высоту контента
      await new Promise(resolve => setTimeout(resolve, 100));
      const actualHeight = printContainer.scrollHeight;

      // Создаем canvas из временного контейнера
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: printContainer.scrollWidth,
        height: actualHeight // Используем реальную высоту контента
      });

      // Удаляем временные элементы
      document.body.removeChild(printContainer);
      document.head.removeChild(styleElement);

      // Создаем PDF с правильными отступами для экспорта
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Размеры PDF страницы A4 в мм
      const pdfWidth = 210;
      const pdfHeight = 297;
      const contentWidth = pdfWidth - pageMargins.left - pageMargins.right;
      const maxContentHeight = pdfHeight - pageMargins.top - pageMargins.bottom;
      
      // Соотношение пикселей к мм (с учетом scale: 2)
      const scale = 2;
      const pxPerMm = canvas.width / contentWidth; // пикселей на мм
      
      // Высота контента одной страницы в пикселях
      const pageHeightPx = maxContentHeight * pxPerMm;
      
      // Количество страниц
      const totalPages = Math.ceil(canvas.height / pageHeightPx);
      
      console.log('PDF Export Debug:', {
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        contentWidth,
        maxContentHeight,
        pxPerMm,
        pageHeightPx,
        totalPages
      });
      
      // Создаем временный canvas для нарезки страниц
      const pageCanvas = document.createElement('canvas');
      const pageCtx = pageCanvas.getContext('2d');
      pageCanvas.width = canvas.width;
      
      for (let page = 0; page < totalPages; page++) {
        // Вычисляем высоту текущей страницы (последняя может быть меньше)
        const startY = page * pageHeightPx;
        const currentPageHeightPx = Math.min(pageHeightPx, canvas.height - startY);
        
        // Устанавливаем высоту canvas для текущей страницы
        pageCanvas.height = currentPageHeightPx;
        
        // Очищаем и рисуем часть исходного canvas
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0, startY,                           // Откуда вырезаем (x, y)
          canvas.width, currentPageHeightPx,   // Размер вырезаемой области
          0, 0,                                // Куда вставляем
          canvas.width, currentPageHeightPx    // Размер вставки
        );
        
        // Конвертируем в изображение
        const pageImgData = pageCanvas.toDataURL('image/png');
        
        // Добавляем новую страницу (кроме первой)
        if (page > 0) {
          pdf.addPage();
        }
        
        // Размер изображения на странице PDF
        const imgWidth = contentWidth;
        const imgHeight = (currentPageHeightPx / pxPerMm);
        
        console.log(`Page ${page + 1}/${totalPages}: imgHeight = ${imgHeight}mm, startY = ${startY}px`);
        
        // Добавляем изображение с отступами
        pdf.addImage(pageImgData, 'PNG', pageMargins.left, pageMargins.top, imgWidth, imgHeight);
      }

      pdf.save('document.pdf');
      setSnackbar({ open: true, message: 'PDF успешно сохранен!', severity: 'success' });
    } catch (error) {
      console.error('Ошибка экспорта PDF:', error);
      setSnackbar({ open: true, message: 'Ошибка при экспорте PDF', severity: 'error' });
    } finally {
      printContainer?.remove();
      styleElement?.remove();
      setIsLoading(false);
    }
  }, [content, pageMargins]);

  // Предпросмотр PDF для печати
  const printDocument = useCallback(async () => {
    setIsLoading(true);
    let printContainer, styleElement;
    try {
      // Создаем временный контейнер для печати без лишних элементов
      printContainer = document.createElement('div');
      printContainer.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: calc(210mm - ${pageMargins.left}mm - ${pageMargins.right}mm);
        background: white;
        padding: 0;
        font-family: 'Times New Roman', serif;
        line-height: 1.6;
        color: #000;
        box-sizing: border-box;
      `;
      
      // Копируем только контент редактора (сохраняем все inline стили включая размер шрифта)
      const editorContent = editorRef.current ? documentHTML(editorRef.current) : content;
      printContainer.innerHTML = editorContent;
      
      // Очищаем и настраиваем таблицы для правильного отображения
      const tables = printContainer.querySelectorAll('table');
      tables.forEach(table => {
        // Убираем фиксированную ширину
        table.style.width = '100%';
        table.style.maxWidth = '100%';
        table.removeAttribute('width');
        
        // Очищаем стили ячеек от старых пиксельных значений
        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          if (cell.style.width && cell.style.width.includes('px')) {
            cell.style.width = '';
          }
          if (cell.style.maxWidth && cell.style.maxWidth.includes('px')) {
            cell.style.maxWidth = '';
          }
          if (cell.style.minWidth && cell.style.minWidth.includes('px')) {
            cell.style.minWidth = '';
          }
          cell.removeAttribute('width');
        });
      });
      
      // Добавляем стили для таблиц в временный контейнер
      styleElement = document.createElement('style');
      styleElement.textContent = `
        table {
          border-collapse: collapse !important;
          width: 100% !important;
          margin: 15px 0 !important;
          border: 2px solid #000 !important;
        }
        td, th {
          border: 1px solid #000 !important;
          padding: 12px !important;
          text-align: left !important;
          vertical-align: top !important;
        }
        th {
          background-color: #f0f0f0 !important;
          font-weight: bold !important;
        }
      `;
      
      document.body.appendChild(printContainer);
      document.head.appendChild(styleElement);

      // Ждем рендеринга и получаем реальную высоту контента
      await new Promise(resolve => setTimeout(resolve, 100));
      const actualHeight = printContainer.scrollHeight;

      // Создаем canvas из временного контейнера
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: printContainer.scrollWidth,
        height: actualHeight // Используем реальную высоту контента
      });

      // Удаляем временные элементы
      document.body.removeChild(printContainer);
      document.head.removeChild(styleElement);

      // Создаем PDF с правильными отступами для печати
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Размеры PDF страницы A4 в мм
      const pdfWidth = 210;
      const pdfHeight = 297;
      const contentWidth = pdfWidth - pageMargins.left - pageMargins.right;
      const maxContentHeight = pdfHeight - pageMargins.top - pageMargins.bottom;
      
      // Соотношение пикселей к мм
      const pxPerMm = canvas.width / contentWidth;
      
      // Высота контента одной страницы в пикселях
      const pageHeightPx = maxContentHeight * pxPerMm;
      
      // Количество страниц
      const totalPages = Math.ceil(canvas.height / pageHeightPx);
      
      // Создаем временный canvas для нарезки страниц
      const pageCanvas = document.createElement('canvas');
      const pageCtx = pageCanvas.getContext('2d');
      pageCanvas.width = canvas.width;
      
      for (let page = 0; page < totalPages; page++) {
        // Вычисляем высоту текущей страницы
        const startY = page * pageHeightPx;
        const currentPageHeightPx = Math.min(pageHeightPx, canvas.height - startY);
        
        // Устанавливаем высоту canvas для текущей страницы
        pageCanvas.height = currentPageHeightPx;
        
        // Очищаем и рисуем часть исходного canvas
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0, startY,
          canvas.width, currentPageHeightPx,
          0, 0,
          canvas.width, currentPageHeightPx
        );
        
        // Конвертируем в изображение
        const pageImgData = pageCanvas.toDataURL('image/png');
        
        // Добавляем новую страницу (кроме первой)
        if (page > 0) {
          pdf.addPage();
        }
        
        // Размер изображения на странице PDF
        const imgWidth = contentWidth;
        const imgHeight = (currentPageHeightPx / pxPerMm);
        
        // Добавляем изображение с отступами
        pdf.addImage(pageImgData, 'PNG', pageMargins.left, pageMargins.top, imgWidth, imgHeight);
      }

      // Вместо сохранения - открываем PDF в новом окне для предпросмотра и печати
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Открываем PDF в новом окне
      const printWindow = window.open(pdfUrl, '_blank');
      if (!printWindow) {
        URL.revokeObjectURL(pdfUrl);
        throw new Error('Браузер заблокировал окно печати');
      }
      if (printWindow) {
        printWindow.onload = function() {
          // Автоматически открываем диалог печати
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
      }
      
      // Очищаем URL через некоторое время
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 10000);
      
      setSnackbar({ open: true, message: 'PDF открыт для печати!', severity: 'success' });
    } catch (error) {
      console.error('Ошибка создания PDF для печати:', error);
      setSnackbar({ open: true, message: 'Ошибка при создании PDF для печати', severity: 'error' });
    } finally {
      printContainer?.remove();
      styleElement?.remove();
      setIsLoading(false);
    }
  }, [content, pageMargins]);

  // Экспорт в DOCX
  const exportToWord = useCallback(async () => {
    setIsLoading(true);
    try {
      const element = editorRef.current;
      if (!element) {
        throw new Error('Элемент редактора не найден');
      }

      const contentHTML = documentHTML(element);
      const converted = htmlDocx.asBlob(contentHTML);
      
      const url = URL.createObjectURL(converted);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: 'DOCX успешно сохранен!', severity: 'success' });
    } catch (error) {
      console.error('Ошибка экспорта DOCX:', error);
      setSnackbar({ open: true, message: 'Ошибка при экспорте DOCX', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Box sx={{ maxWidth: '100%', margin: '0 auto' }}>
      {/* Подключаем динамические стили */}
      <style>{getDocumentStyles(pageMargins)}</style>
      
      {/* Зафиксированная панель инструментов */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '2px solid #ddd',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: 2,
          paddingTop: 1,
          paddingBottom: 1,
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <EditorToolbar
          onInsertTable={handleInsertTable}
          onInsertVariable={showVariables ? handleInsertVariable : undefined}
          onPageMargins={!readOnly ? handlePageMargins : undefined}
          onSave={handleSave}
          onPrint={printDocument}
          onExportPDF={exportToPDF}
          onExportWord={exportToWord}
          editorRef={editorRef}
          forceUpdateContent={forceUpdateContent}
          readOnly={readOnly}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
        />
      </Box>
      
      {/* Основная рабочая область с документом */}
      <Box sx={{ padding: 2 }}>
        {/* Простой ContentEditable редактор в рамке A4 */}
        <Box 
          className="document-canvas"
        >
        <div
          ref={editorRef}
          className="editor-container"
          contentEditable={!readOnly} // Добавляем возможность отключить редактирование
          suppressContentEditableWarning={true}
          onInput={readOnly ? undefined : (e) => {
            handleContentChange(e);
            // Добавляем в историю с задержкой
            debouncedAddToHistory(e.target.innerHTML);
          }}
          onKeyDown={readOnly ? undefined : handleKeyDown} // Добавляем обработчик клавиш
          onPaste={readOnly ? undefined : (e) => {
            // Обработка вставки - добавляем в историю с задержкой
            setTimeout(() => {
              if (!editorRef.current) return;
              const newContent = editorRef.current.innerHTML;
              setContent(newContent);
              onContentChange?.(newContent);
              debouncedAddToHistory(newContent);
              setupTables();
            }, 100);
          }}
          onCut={readOnly ? undefined : (e) => {
            // Обработка вырезания - добавляем в историю с задержкой
            setTimeout(() => {
              if (!editorRef.current) return;
              const newContent = editorRef.current.innerHTML;
              setContent(newContent);
              onContentChange?.(newContent);
              debouncedAddToHistory(newContent);
            }, 100);
          }}
          onClick={() => {
            // Закрываем инструменты таблицы при клике по документу
            if (selectedTable) {
              handleCloseTableTools();
            }
          }}
          style={{
            outline: 'none',
            border: 'none',
            cursor: readOnly ? 'default' : 'text' // Меняем курсор в зависимости от режима
          }}
        />
      </Box>
      </Box> {/* Закрываем основную рабочую область */}

      {/* Модальное окно для создания таблицы */}
      <Dialog 
        open={tableDialogOpen} 
        onClose={() => setTableDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Создать таблицу</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Количество строк"
              type="number"
              value={tableRows}
              onChange={(e) => setTableRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: 20 }}
              fullWidth
            />
            <TextField
              label="Количество столбцов"
              type="number"
              value={tableCols}
              onChange={(e) => setTableCols(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: 10 }}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              Будет создана таблица {tableRows} строк × {tableCols} столбцов
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>Отмена</Button>
          <Button onClick={confirmInsertTable} variant="contained">
            Вставить таблицу
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно для переменных */}
      {showVariables && (
        <Dialog 
          open={variableDialogOpen} 
          onClose={() => setVariableDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Выберите переменную</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {variables.map((variable) => (
                <Button
                  key={variable.name}
                  onClick={() => confirmInsertVariable(variable)}
                  sx={{ 
                    justifyContent: 'flex-start', 
                    textTransform: 'none',
                    p: 2
                  }}
                  variant="outlined"
                >
                  <Box>
                    <Typography variant="subtitle1" component="div">
                      {`{${variable.name}}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {variable.description}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVariableDialogOpen(false)}>Отмена</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Модальное окно для настройки отступов страницы */}
      <Dialog 
        open={marginsDialogOpen} 
        onClose={() => setMarginsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Настройка отступов страницы</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Верхний отступ (мм)"
              type="number"
              value={draftMargins.top}
              onChange={(e) => handleMarginsChange('top', e.target.value)}
              inputProps={{ min: 0, max: 50, step: 1 }}
              fullWidth
              helperText="Отступ от верхнего края страницы"
            />
            <TextField
              label="Нижний отступ (мм)"
              type="number"
              value={draftMargins.bottom}
              onChange={(e) => handleMarginsChange('bottom', e.target.value)}
              inputProps={{ min: 0, max: 50, step: 1 }}
              fullWidth
              helperText="Отступ от нижнего края страницы"
            />
            <TextField
              label="Левый отступ (мм)"
              type="number"
              value={draftMargins.left}
              onChange={(e) => handleMarginsChange('left', e.target.value)}
              inputProps={{ min: 0, max: 50, step: 1 }}
              fullWidth
              helperText="Отступ от левого края страницы"
            />
            <TextField
              label="Правый отступ (мм)"
              type="number"
              value={draftMargins.right}
              onChange={(e) => handleMarginsChange('right', e.target.value)}
              inputProps={{ min: 0, max: 50, step: 1 }}
              fullWidth
              helperText="Отступ от правого края страницы"
            />
            <Typography variant="body2" color="text.secondary">
              Предпросмотр: отступы {draftMargins.top}мм (верх) × {draftMargins.right}мм (право) × {draftMargins.bottom}мм (низ) × {draftMargins.left}мм (лево)
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarginsDialogOpen(false)}>Отмена</Button>
          <Button onClick={applyMargins} variant="contained">
            Применить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Индикатор загрузки */}
      {isLoading && (
        <Backdrop open={isLoading} sx={{ zIndex: 9999 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {/* Инструменты таблицы */}
      <TableTools
        table={selectedTable}
        position={tableToolsPosition}
        onClose={handleCloseTableTools}
        onTableUpdate={handleTableUpdate}
      />
    </Box>
  );
}

export default DocumentEditor;
